import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/react'

// Components
import { Button } from '../Button'

/**
 * Renders in place of a tree that threw while rendering.
 * - The reference is the Sentry event id, so a reported crash can be found again.
 */
const ErrorFallback = ({ eventId, resetError }) => (
  <div className="flex h-full w-full flex-col items-center justify-center bg-white px-4 py-16 text-center">
    <h1 className="text-2xl font-bold tracking-tight text-gray-900">Something went wrong.</h1>
    <p className="mt-2 max-w-md text-base text-gray-500">
      This page ran into a problem and could not be shown. Your last change may not have been
      saved. Try again, or reload the page.
    </p>

    <div className="mt-6 flex justify-center gap-3">
      <Button background="bg-white" label="Try Again" onClick={resetError} outlined />
      <Button
        background="bg-purple border-purple hover:bg-purple-600"
        label="Reload Page"
        onClick={() => window.location.reload()}
      />
    </div>

    {eventId && <p className="mt-6 text-xs text-gray-400">Reference: {eventId}</p>}
  </div>
)

ErrorFallback.defaultProps = {
  eventId: null,
}

ErrorFallback.propTypes = {
  eventId: PropTypes.string,
  resetError: PropTypes.func.isRequired,
}

/**
 * Catches render errors below it, reports them to Sentry, and shows a fallback instead of
 * unmounting the whole app to a blank screen.
 * - When `resetKey` changes, a caught error is cleared. Pass the path so that leaving the broken
 *   page shows the next page. A `key` would do the same, but it also remounts every page on
 *   every navigation, even when nothing has failed.
 */
const ErrorBoundary = ({ children, name, resetKey = null }) => {
  const boundaryRef = useRef(null)

  useEffect(() => {
    if (boundaryRef.current?.state.error) boundaryRef.current.resetErrorBoundary()
  }, [resetKey])

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => scope.setTag('error_boundary', name)}
      fallback={ErrorFallback}
      ref={boundaryRef}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  resetKey: PropTypes.string,
}

export default ErrorBoundary
